import React, { useEffect, useMemo, useState } from 'react';

import { ExclamationCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { MenuProps } from 'antd/es/menu';
import { Spin } from 'antd';
import { useSimpleModal } from '../Modal/Simple';
import { CommentData,
  useCommentChildrenListGet,
  useCommentDelete,
  useCommentHide,
  useCommentRestore } from '../../../hooks/api/comments';
import PostOrCommentTemplate, { useUniqueData } from '../PostOrCommentTemplate';
import SubComment from './SubComment';
import ReasonModal from '../ReasonModal';
import styles from '../PostOrCommentTemplate/index.module.scss';
import useComponentDidUpdate from '../../../hooks/componentDidUpdate';

export interface CommentProps {
  data: CommentData;
  setPage: (page: number) => void;
}

export default function Comment({ data, setPage }: CommentProps): JSX.Element {
  const navigate = useNavigate();
  const { modal, contextHolder } = useSimpleModal();
  const [open, setOpen] = useState(false);
  const [openHideComment, setOpenHideComment] = useState(false);
  const commentChildrenListGet = useCommentChildrenListGet(data.id);
  const commentDelete = useCommentDelete(data.id);
  const commentHide = useCommentHide();
  const commentRestore = useCommentRestore();
  const [subCommentPage, setSubCommentPage] = useState(1);
  const [commentListAcc] = useUniqueData<CommentData>(commentChildrenListGet, subCommentPage);

  useEffect(() => {
    if (commentDelete.data && !commentDelete.error && !commentDelete.loading) {
      setPage(1);
    }
  }, [commentDelete.data]);

  useEffect(() => {
    if (commentHide.data && !commentHide.error && !commentHide.loading) {
      setOpenHideComment(false);
      setPage(1);
    }
  }, [commentHide.data]);

  useEffect(() => {
    if (commentRestore.data && !commentRestore.error && !commentRestore.loading) {
      setPage(1);
    }
  }, [commentRestore.data]);

  useEffect(() => {
    if (open && commentListAcc.list.length === 0) {
      commentChildrenListGet.fetch({
        page: subCommentPage,
      });
    }
  }, [open]);

  useComponentDidUpdate(() => {
    commentChildrenListGet.fetch({
      page: subCommentPage,
    });
  }, [subCommentPage]);

  const confirmDelete = () => {
    if (data.id) {
      modal.confirm({
        title: 'Are you sure you want to Delete the comment?',
        icon: <ExclamationCircleOutlined />,
        onOk: () => commentDelete.fetch(),
        okText: 'Delete',
      });
    }
  };

  const hideComment = (reason: string) => {
    commentHide.fetch({ reason }, `${data.id}/hide`);
  };

  const confirmRestore = () => {
    modal.confirm({
      title: 'Are you sure you want to Publish the comment?',
      icon: <ExclamationCircleOutlined />,
      onOk: () => commentRestore.fetch(undefined, `${data.id}/restore`),
      okText: 'Publish',
    });
  };

  const dropdownMenu: MenuProps = useMemo(
    () => ({
      items: [
        {
          key: '1',
          label: 'Edit',
          onClick: () => navigate(`/comments/${data.id}/update`),
        },
        {
          key: '2',
          label: 'Delete',
          onClick: confirmDelete,
        },
        !data.isHidden
          ? {
            key: '3',
            label: 'Hide',
            onClick: () => setOpenHideComment(true),
          }
          : {
            key: '3',
            label: 'Publish',
            onClick: confirmRestore,
          },
      ],
    }),
    [data],
  );

  const childrenList = useMemo(
    () => [
      ...(commentListAcc?.list.map((comment) => (
        <SubComment
          setPage={(newPage) => {
            setSubCommentPage(newPage);
            commentChildrenListGet.fetch({
              page: newPage,
            });
          }}
          key={comment.id}
          data={comment}
        />
      )) || []),
      commentChildrenListGet.loading ? (
        <Spin key="loading" indicator={<LoadingOutlined spin />} className={styles.spin} />
      ) : undefined,
    ].filter((value) => value),
    [commentListAcc?.list, commentChildrenListGet.loading],
  );

  return (
    <>
      <PostOrCommentTemplate
        loading={commentChildrenListGet.loading}
        open={open}
        setOpen={data.numberOfComments ? setOpen : undefined}
        nextPage={() => setSubCommentPage(subCommentPage + 1)}
        data={data}
        type="comments"
        dropdownMenu={dropdownMenu}
        showMoreText={
          commentListAcc.lastMeta?.hasNextPage && !commentChildrenListGet.loading ? 'Show more subComment' : undefined
        }
        childrenList={open ? childrenList : undefined}
      />
      <ReasonModal
        open={openHideComment}
        setOpen={setOpenHideComment}
        title="Hide comment?"
        actionButtonText="Hide comment"
        placeholder="Why is this comment being hidden?"
        subtitle="The comment will no longer be visible to other users."
        loading={commentHide.loading}
        onSubmit={hideComment}
      />
      {contextHolder}
    </>
  );
}
